import React, { useEffect, useMemo, useState } from "react";
import styles from "./payment-history.module.css";
import CustomTable from "../../../../common/custom-table/custom-table";
import CustomFilter from "../../../../components/custom-filter/custom-filter";
import CustomDateRangePicker from "../../../../common/input/custom-daterange-picker/custom-daterange-picker";
import dayjs from "dayjs";
import CustomSelect from "../../../../common/input/custom-select/custom-select";
import CustomButton from "../../../../common/custom-button/custom-button";
import {
  getInstitutionPaymentHistory,
  getPaymentHistoryForExport,
} from "../../../../services/institution";
import { toast } from "react-toastify";
import { useLocation } from "react-router-dom";

const SORT = [
  {
    label: "Sort by: All",
    value: "all",
  },
  {
    label: "Sort by: Date ASC",
    value: "date-asc",
  },
  {
    label: "Sort by: Date DESC",
    value: "date-desc",
  },
];

const PaymentHistory = () => {
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [sortSelected, setSortSelected] = useState(SORT[0]);
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(7, "days"),
    dayjs(),
  ]);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const institutionId = queryParams.get("uid");

  const columns = useMemo(
    () => [
      {
        title: "Date",
        dataIndex: "date",
        key: "date",
        render: (value) => (
          <div className={styles.tableRow}>{value || "-"}</div>
        ),
      },
      {
        title: "Cheque/ Ref No",
        dataIndex: "referenceNumber",
        key: "referenceNumber",
        render: (value) => <div className={styles.tableRow}>{value}</div>,
      },
      {
        title: "Credit Amount",
        dataIndex: "amount",
        key: "amount",
        render: (value) => (
          <div className={styles.tableRowAmount}>+{value}</div>
        ),
      },
      {
        title: "Payment Method",
        dataIndex: "paymentMethod",
        key: "paymentMethod",
        render: (value) => (
          <div className={styles.tablePaymentMethod}>{value}</div>
        ),
      },
    ],
    []
  );

  const handleChangePage = (page, pageSize) => {
    setPageNo(page);
    setPageSize(pageSize);
  };

  const handleChange = (selectedIdx) => {
    setSortSelected(SORT[selectedIdx]);
  };

  const handleExportCSV = async () => {
    try {
      setLoading(true);
      const payload = {
        start_date: dateRange[0]?.format("YYYY-MM-DD"),
        end_date: dateRange[1]?.format("YYYY-MM-DD"),
        institution_id: institutionId,
      };
      const response = await getPaymentHistoryForExport(payload);

      if (response?.status) {
        setLoading(false);
        const blob = new Blob([response?.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `payment history for ${dateRange[0]?.format(
            "YYYY-MM-DD"
          )} to ${dateRange[1]?.format("YYYY-MM-DD")}.xlsx`
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to export file.");
    }
  };

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const resp = await getInstitutionPaymentHistory();
        setPaymentHistory(resp?.data?.data ?? []);
      } catch (err) {
        console.log(err);
        toast.error("Something Went Wrong");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className={styles.PaymentHistory}>
      <div className={styles.headerContainer}>
        <div className={styles.title}>Transactions History</div>
        <div className={styles.filterContainer}>
          <div className={styles.innerFilterContainer}>
            <CustomFilter width={styles.filter} />
            <CustomDateRangePicker
              dateFormat="MMM DD, YYYY"
              value={dateRange}
              onChange={(val) => setDateRange(val)}
            />
          </div>
          <div className={styles.innerFilterContainer}>
            <CustomSelect
              className={styles.customSelectSort}
              onChange={handleChange}
              value={sortSelected.label}
            >
              {SORT?.map((item, key) => {
                return (
                  <div key={key} style={{ margin: 0, height: 0 }}>
                    {item.label}
                  </div>
                );
              })}
            </CustomSelect>
            <CustomButton
              title="Export CSV"
              iconPosition="end"
              onClick={handleExportCSV}
              style={{
                backgroundColor: "#39B54A1A",
                color: "#39B54A",
                border: "none",
                height: "40px",
              }}
              icon={
                <img src="/assets/images/exportIcon.svg" alt="exportIcon" />
              }
            />
          </div>
        </div>
      </div>
      <div className={styles.tableContainer}>
        <CustomTable
          columns={columns}
          dataSource={paymentHistory}
          total={totalCount}
          loading={loading}
          showMoreIcon={true}
          pageSize={pageSize}
          currentPage={pageNo}
          handlePageChange={handleChangePage}
        />
      </div>
    </div>
  );
};

export default PaymentHistory;
